import { createContext } from 'react'
import { FormProvider } from 'react-hook-form'
import { FormInput } from '@microservices/wiskey-react-components'
import { Box, Grid, Typography } from '@mui/material'

import { MainTabs } from '@pages/WindowsCreateOrEdit/components/WindowMainTabs'
import { useWindowsCreateOrEdit } from '@pages/WindowsCreateOrEdit/hooks/useWindowsCreateOrEdit'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { ENTITY_CONFIG_TYPE, GENERATOR_INPUT_TYPE, REGEX } from '@constants'

import { WindowsCreateOrEditContextType } from './types/types'

export const WindowsCreateOrEditContext = createContext<WindowsCreateOrEditContextType>(
  {} as WindowsCreateOrEditContextType
)

export const WindowsCreateOrEdit = () => {
  const { state, handlers, data } = useWindowsCreateOrEdit()

  const { currentTab, allWindows } = state
  const { handleTabChange, handleSave, handleCancel } = handlers
  const {
    isCreate,
    methods,
    t,
    autocompleteOptionsFilterSections,
    autocompleteOptionsFilterSA,
    isLoadingSearchAssistantsData,
    isLoadingWindowById,
    isLoadingWindow,
    isLoadingWindows,
    isDirty,
    windowById,
    isEdit,
    windowId,
    windowVariables,
  } = data

  const contextValue = {
    isCreateWindow: isCreate,
    isLoadingWindowById,
    windowById,
    isEdit,
    windowId,
    windowVariables,
  } as WindowsCreateOrEditContextType

  return (
    <WindowsCreateOrEditContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.WINDOWS}>
        <PageContentLayout
          sx={{
            p: 1,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <EditConfigScriptDialog />
          <Box sx={{ flexShrink: 0 }}>
            <Grid container alignItems={'center'} justifyContent={'space-between'} py={0.5}>
              <Typography mb={1} variant='h5'>
                {isCreate ? t(`window.title.create`) : t(`window.title.edit`)}
              </Typography>
              {windowId && <EditConfigMenu configId={windowId} />}
            </Grid>
            <FormProvider {...methods}>
              <FormInput
                disabled={!isCreate}
                inputType={GENERATOR_INPUT_TYPE.INPUT}
                label={t('window.form.internalId.label')}
                loading={isLoadingWindowById}
                name={'internalId'}
                placeholder={t('window.form.internalId.placeholder')}
                replacePattern={REGEX.MODEL_CODE_REPLACE_PATTERN}
                rules={{
                  required: true,
                  validate: value => {
                    if (
                      isCreate &&
                      allWindows &&
                      allWindows?.find(window => window.internalId === value)
                    ) {
                      return `${value} ${t('window.errors.alreadyUsed')}`
                    }
                  },
                }}
              />
              <Grid container gap={1}>
                <Grid item flexGrow={1}>
                  <FormInput
                    inputType={GENERATOR_INPUT_TYPE.INPUT}
                    label={t('window.form.windowTitle.label')}
                    loading={isLoadingWindowById}
                    name={'title'}
                    placeholder={t('window.form.windowTitle.placeholder')}
                    rules={{
                      required: true,
                      validate: value => value.trim().length !== 0,
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item sx={{ width: '50%' }}>
                  <FormInput
                    autocompleteOptions={autocompleteOptionsFilterSections}
                    inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                    label={t('window.form.filterSection.label')}
                    loading={isLoadingWindowById}
                    name={'filterSection'}
                  />
                </Grid>
                <Grid item sx={{ width: '50%' }}>
                  <FormInput
                    autocompleteOptions={autocompleteOptionsFilterSA}
                    formLabelSx={{ pl: 3 }}
                    inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                    label={t('window.form.filter.label')}
                    loading={isLoadingSearchAssistantsData}
                    name={'filter'}
                  />
                </Grid>
              </Grid>
            </FormProvider>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            <FormProvider {...methods}>
              <MainTabs currentTab={currentTab} handleTabChange={handleTabChange} />
            </FormProvider>
          </Box>

          <Box sx={{ flexShrink: 0, pt: 1 }}>
            <Grid container gap='15px' justifyContent={'flex-end'} width={'100%'}>
              <GeneralButtons
                cancelText={t('window.buttons.cancel')}
                isLoading={isLoadingWindow || isLoadingWindows || isLoadingWindowById}
                saveDisabled={
                  !isDirty || isLoadingWindow || isLoadingWindows || isLoadingWindowById
                }
                saveText={t('window.buttons.save')}
                onCancel={handleCancel}
                onSave={handleSave}
              />
            </Grid>
          </Box>
        </PageContentLayout>
      </EditConfigProvider>
    </WindowsCreateOrEditContext.Provider>
  )
}
